import React from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AiReports } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/Templates';
import {
  DashboardConfig,
  ModuleConfig,
} from '@lifeomic/phc-web-toolkit/src/components/LayoutConfiguration/types';
import { useHistory, useParams } from 'react-router-dom';
import urls from '../../../../util/urls';
import MemberDetail from './index';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.palette.background.paper,
    height: '100%',
  },
}));

const zoriReportsModuleConfig: ModuleConfig<{ displayAsDuration: boolean }> = {
  version: 1,
  module: {
    key: 'member-zori-reports',
    moduleType: 'd7af2393-f012-40e9-bb34-2a09e86a89b0',
    title: 'Zori Reports',
    layout: {
      w: 12,
      h: 20,
      minW: 1,
      minH: 1,
      x: 0,
      y: 0,
      isResizable: true,
    },
  },
  component: { displayAsDuration: false },
};

const zoriReportsDashboardConfig: DashboardConfig = {
  version: 1,
  modules: [zoriReportsModuleConfig],
};

const zoriReportsInjectedPropsBase = {
  moduleKey: zoriReportsModuleConfig.module.key,
  isModuleVisible: true,
  dateTickFilter: { min: 0, max: 0 },
  dateTickBounds: { min: 0, max: 0 },
  dateFilter: { min: new Date(0), max: new Date() },
  isInEditMode: false,
  deviceId: 'member-detail',
  moduleConfig: zoriReportsModuleConfig,
  onModuleConfigChange: () => undefined,
  onDynamicDataResolved: () => ({}),
  selectedDashboardConfig: zoriReportsDashboardConfig,
} as const;

const SELECTED_REPORT_STORAGE_KEY = 'ai-tools-selected-report-id';

type ZoriReportsContentProps = {
  member: fhir.Patient | null;
  memberId: string;
  fallback: React.ReactNode;
};

export const ZoriReportsContent: React.FC<ZoriReportsContentProps> = ({
  member,
  memberId,
  fallback,
}) => {
  const classes = useStyles({});
  const history = useHistory();
  const { aiToolsSubroute } = useParams<{ aiToolsSubroute?: string }>();

  const resolvedSubroute = aiToolsSubroute || 'all';
  const isTemplatesRoute = resolvedSubroute === 'templates';
  const isAllRoute = resolvedSubroute === 'all';
  const selectedReportId =
    isTemplatesRoute || isAllRoute ? null : resolvedSubroute;
  const selectedTabId: 'reports' | 'templates' = isTemplatesRoute
    ? 'templates'
    : 'reports';
  const getStoredReportId = () =>
    window.localStorage.getItem(SELECTED_REPORT_STORAGE_KEY);

  React.useEffect(() => {
    if (selectedReportId) {
      window.localStorage.setItem(
        SELECTED_REPORT_STORAGE_KEY,
        selectedReportId,
      );
    }
  }, [selectedReportId]);

  React.useEffect(() => {
    if (!aiToolsSubroute) {
      history.replace(`${urls.app.members(memberId)}/ai-tools/all`);
    }
  }, [aiToolsSubroute, history, memberId]);

  if (!member) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.container}>
      <AiReports
        {...zoriReportsInjectedPropsBase}
        selectedTabId={selectedTabId}
        onTabChange={tab => {
          if (tab === 'templates') {
            history.push(`${urls.app.members(memberId)}/ai-tools/templates`);
            return;
          }
          const storedReportId = getStoredReportId();
          if (storedReportId) {
            history.push(
              `${urls.app.members(memberId)}/ai-tools/${storedReportId}`,
            );
            return;
          }
          history.push(`${urls.app.members(memberId)}/ai-tools/all`);
        }}
        selectedReportId={selectedReportId}
        onSelectedReportIdChange={id => {
          if (!id) {
            window.localStorage.removeItem(SELECTED_REPORT_STORAGE_KEY);
            history.push(`${urls.app.members(memberId)}/ai-tools/all`);
            return;
          }
          window.localStorage.setItem(SELECTED_REPORT_STORAGE_KEY, id);
          history.push(`${urls.app.members(memberId)}/ai-tools/${id}`);
        }}
        patient={{
          ...member,
          id: member.id || memberId,
        }}
      />
    </div>
  );
};

const MemberAiTools: React.FC = () => {
  return <MemberDetail section="ai-tools" />;
};

MemberAiTools.displayName = 'MemberAiTools';
export default MemberAiTools;
